import * as React from 'react';
import PropTypes from 'prop-types';
import { API_URL_BASE, RESTService, LoginService, SpectrumService } from "./server_calls";
import AppTemplate from './AppDefinitions';

class APIService extends React.Component {

    //==================================================================================================================
    //
    //                                             Props and State
    //
    //==================================================================================================================

    static propTypes = {
        apiTasks                : PropTypes.array.isRequired,
        appState                : PropTypes.object, 
        onStateChange           : PropTypes.func,
        onProjectionAdd         : PropTypes.func,
    };

    static defaultProps = {
        apiTasks                : [],
        appState                : {}, 
        onStateChange           : () => console.log('onStateChange'),
        onProjectionAdd         : () => console.log('onProjectionAdd'),
    };

    componentDidUpdate() {
        this.onHandleTasks();
    }

    //==================================================================================================================
    //
    //                                              Event Handlers
    //
    //==================================================================================================================

    onHandleTasks = () => {
        let i;
        let task = null;
        let tasks = this.props.apiTasks;
        let len = tasks.length;

        for (i = 0; i < len; i++) {
            task = tasks[i];

            if (!task.complete && !task.running) {
                this.RunServiceCall(
                    task.flag,
                    task.params,
                    task.successFn,
                    task.errorFn,
                    task
                );

                // Only call one API call at a time.  Rerendering the app will continue to go through the list.
                return;
            }
        }
    };

    onIncrementCallsSent = (task) => {
        this.props.onStateChange({
            apiTasks  : this.props.apiTasks.map(el => (el.GUID === task.GUID ? {
                ...el, 
                running : true, 
                complete : false, 
                success : false, 
                error : false, 
                fail : false,
                sentTime : new Date().toTimeString()
            } : el))
        });
    };

    onIncrementCallsSuccess = (task) => {
        this.props.onStateChange({
            heartBeat : 0,
            apiTasks  : this.props.apiTasks.map(el => (el.GUID === task.GUID ? {
                ...el, 
                running : false, 
                complete : true, 
                success : true, 
                error : false, 
                fail : false,
                returnTime : new Date().toTimeString()
            } : el))
        });
    };

    onIncrementCallsError = (task) => {
        this.props.onStateChange({
            apiTasks  : this.props.apiTasks.map(el => (el.GUID === task.GUID ? {
                ...el, 
                running : false, 
                complete : true, 
                success : false, 
                error : true, 
                fail : false,
                returnTime : new Date().toTimeString()
            } : el))         
        });
    };

    onIncrementCallsFail = (task) => {
        this.props.onStateChange({
            apiTasks  : this.props.apiTasks.map(el => (el.GUID === task.GUID ? {
                ...el, 
                running : false, 
                complete : true, 
                success : false, 
                error : false, 
                fail : true,
                returnTime : new Date().toTimeString()
            } : el))           
        });
    };

    RunServiceCall = (flag, params, successFn, errorFn, task) => {
        let onIncrementCallsSent    = this.onIncrementCallsSent;
        let onIncrementCallsSuccess = this.onIncrementCallsSuccess;
        let onIncrementCallsError   = this.onIncrementCallsError;
        let onIncrementCallsFail    = this.onIncrementCallsFail;
        let func                    = () => {};

        if (typeof successFn === 'undefined') {
            successFn = () => {};
        }

        if (typeof errorFn === 'undefined') {
            errorFn = (msg) => {
                console.error('Caught error: ' + msg);
                alert('Caught error: ' + msg);
            };
        }

        func = this.onGetAPICall(flag);

        if (func === null) {
            onIncrementCallsSent(task);
            onIncrementCallsFail(task);
            errorFn('API call [' + flag + '] is not available');
        }
        else {
            try {
                onIncrementCallsSent(task);
    
                func(params,
                    (response) => {
                        onIncrementCallsSuccess(task);
                        successFn(response);
                    },
                    (msg) => {
                        onIncrementCallsError(task);
                        errorFn(msg);
                    }
                );
            }
            catch(e) {
                onIncrementCallsFail(task);
                errorFn(e.message);
            }
        }
    };

    //==================================================================================================================
    //
    //                                                  API
    //
    //==================================================================================================================

    onGetAPICall = (flag) => {

        switch (flag) {

            // Custom calls
            case "fetchSession"             : return this.onFetchSession; 
            case "fetchSessionInfo"         : return this.onFetchSessionInfo; 
            case "fetchSessionGeneral"      : return this.onFetchSessionGeneral; 
            case "fetchSessionInputs"       : return this.onFetchSessionInputs; 
            case "fetchSessionResults"      : return this.onFetchSessionResults; 

            case "sendSession"              : return this.onSendSession; 
            case "sendAndFetchSession"      : return this.onSendAndFetchSession;

            case "createProjectionFlag"     : return this.onCreateProjectionFlag;
            case "modifyProjectionFlag"     : return this.onModifyProjectionFlag;
            case "openProjectionFlag"       : return this.onOpenProjectionFlag;
            case "saveProjectionFlag"       : return this.onSaveProjectionFlag;
            case "closeProjectionFlag"      : return this.onCloseProjectionFlag;
            case "deleteProjectionFlag"     : return this.onDeleteProjectionFlag;
            case "calculateProjectionFlag"  : return this.onCalculateProjectionFlag;
            case "downloadProjectionFlag"   : return this.onDownloadProjectionFlag;
            case "importProjectionFlag"     : return this.onImportProjectionFlag;
            case "getSelectedResultsFlag"   : return this.onGetSelectedResultsFlag;

            // RESTService
            case "ping"                     : return this.onPing;
            case "getCountries"             : return this.onGetCountries;
            case "getVersion"               : return this.onGetVersion;

            // LoginService
            case "getStatus"                : return this.onGetStatus;
            case "signedIn"                 : return this.onSignedIn;
            case "login"                    : return this.onLogin;
            case "loginAsGuest"             : return this.onLoginAsGuest;
            case "logout"                   : return this.onLogout;
            case "getAccountInfo"           : return this.onGetAccountInfo;

            // SpectrumService
            // case "ping"                     : return this.onPing;
            case "keepAlive"                : return this.onKeepAlive;
            case "getEverything"            : return this.onGetEverything;
            case "getActiveProjectionList"  : return this.onGetActiveProjectionList;
            case "getStoredProjectionList"  : return this.onGetStoredProjectionList;

            case "getGBData"                : return this.onGetGBData;
            case "getModvars"               : return this.onGetModvars;
            case "setModvars"               : return this.onSetModvars;
            case "getEditors"               : return this.onGetEditors;
            case "setEditors"               : return this.onSetEditors;
            case "getResultsData"           : return this.onGetResultsData;
            
            case "createProjection"         : return this.onCreateProjection;
            case "modifyProjection"         : return this.onModifyProjection;
            case "openProjection"           : return this.onOpenProjection;
            case "saveProjection"           : return this.onSaveProjection;
            case "closeProjection"          : return this.onCloseProjection;
            case "downloadProjection"       : return this.onDownloadProjection;
            case "deleteProjection"         : return this.onDeleteProjection;
            case "calculateProjection"      : return this.onCalculateProjection;

            case "getResultMenus"           : return this.onGetResultMenus;
            case "getResultTable"           : return this.ongetResultTable;
            case "getResultTables"          : return this.onGetResultTables;
            case "getResultCharts"          : return this.onGetResultCharts;

            default                         : console.error('APIService: flag ' + flag + ' does not exist!'); return null;
        }
    };

    //==================================================================================================================
    //
    //                                               Custom API Calls
    //
    //==================================================================================================================

    onFetchSession = (params, successFn, errorFn) => {

        // this.doRun([
        //     { serviceName : 'getAccountInfo' },
        //     { serviceName : 'GetStatus' },
        //     { serviceName : 'GetActiveProjectionList' },
        //     { serviceName : 'GetStoredProjectionList' },
        //     { serviceName : 'GetGB' },
        //     { serviceName : 'GetCountries' },
        //     { serviceName : 'CalculateProjection' },
        //     { serviceName : 'GetResultMenus' },
        //     { serviceName : 'GetGB' },
        //     { serviceName : 'GetGB' },
        // ]);

        RESTService.ping({}, (r1) => {
            RESTService.getVersion({}, (r11) => {
                RESTService.getCountries({}, (r7) => {
                    LoginService.getAccountInfo({}, (r2) => {
                        LoginService.getStatus({}, (r3) => {
                            if (r2.fSignedIn) {
                                SpectrumService.getActiveProjectionList({}, (r4) => {
                                    SpectrumService.getStoredProjectionList({}, (r5) => {
                                        SpectrumService.getGBData({}, (r6) => {
                                            SpectrumService.getResultMenus({}, (r8) => {
                                                this.onGetModvarsAndEditorsFlag(1, r6.FinalProj, (r9) => {
                                                    SpectrumService.calculateProjection({Proj: 0}, () => {
                                                        SpectrumService.getAllResults({}, (r10) => {
                                                            this.props.onStateChange({
                                                                serverAlive       : r1,
                                                                version           : r11,
        
                                                                signedIn          : r2.fSignedIn,
                                                                userName          : r2.fUserName,
                                                                humanName         : r2.fHumanName,
                                                                isGuest           : r2.fIsGuest,
                                                                isAdmin           : r2.fIsAdmin,
        
                                                                sessionExists     : r3.sessionExists,
                                                                disconnectWarning : r3.disconnectWarning,
                                                                TimeToLive        : r3.TimeToLive,
                                                                TimeIdle          : r3.TimeIdle,
                                                                TimeExpire        : r3.TimeExpire,
                                                                TimeTotal         : r3.TimeTotal,
        
                                                                activeProjections : r4,
                                                                storedProjections : r5,
                                                                GB                : r6,
                                                                countryList       : r7,
        
                                                                DPTree            : r8.TreeDP,
                                                                AMTree            : r8.TreeAM,
                                                            
                                                                DPModvars         : r9.DPModvars,
                                                                DPEditors         : r9.DPEditors, 
                                                                AMModvars         : r9.AMModvars, 
                                                                AMEditors         : r9.AMEditors, 
                                                                
                                                                projectCreated    : r4.length > 0,
                                                                projectionName    : (r4.length > 0) ? r4[0].name : '',
        
                                                                DPResults         : r10.Results.filter(x => (x.Info.modID === 1) ? true : false), 
                                                                AMResults         : r10.Results.filter(x => (x.Info.modID === 4) ? true : false), 
                                                            }, () => successFn({
                                                                r1 : r1,
                                                                r2 : r2,
                                                                r3 : r3,
                                                                r4 : r4,
                                                                r5 : r5,
                                                                r6 : r6,
                                                                r7 : r7,
                                                                r8 : r8,
                                                                r9 : r9,
                                                                r10 : r10,
                                                                r11 : r11
                                                            }));
                                                        }, errorFn);
                                                    }, errorFn);
                                                }, errorFn);
                                            }, errorFn);
                                        }, errorFn);
                                    }, errorFn);
                                }, errorFn);
                            }
                            else {
                                this.props.onStateChange({
                                    serverAlive       : r1,
                                    version           : r11,
                                    countryList       : r7,
                
                                    signedIn          : r2.fSignedIn,
                                    userName          : r2.fUserName,
                                    humanName         : r2.fHumanName,
                                    isGuest           : r2.fIsGuest,
                                    isAdmin           : r2.fIsAdmin,
        
                                    sessionExists     : r3.sessionExists,
                                    disconnectWarning : r3.disconnectWarning,
                                    TimeToLive        : r3.TimeToLive,
                                    TimeIdle          : r3.TimeIdle,
                                    TimeExpire        : r3.TimeExpire,
                                    TimeTotal         : r3.TimeTotal,

                                    pageID            : 'GBLoggedOutFm',
                                }, () => successFn(false));
                            }
                        }, errorFn);
                    }, errorFn);
                }, errorFn);
            }, errorFn);
        }, errorFn);
    };

    onFetchSessionInfo = (params, successFn, errorFn) => {
        RESTService.ping({}, (r1) => {
            LoginService.getAccountInfo({}, (r2) => {
                this.props.onStateChange({
                    serverAlive       : r1,

                    signedIn          : r2.fSignedIn,
                    userName          : r2.fUserName,
                    humanName         : r2.fHumanName,
                    isGuest           : r2.fIsGuest,
                    isAdmin           : r2.fIsAdmin
                }, () => successFn({
                    r1 : r1,
                    r2 : r2   
                }));
            }, errorFn);
        }, errorFn);
    };

    onFetchSessionGeneral = (params, successFn, errorFn) => {
        RESTService.ping({}, (r1) => {
            RESTService.getCountries({}, (r7) => {
                LoginService.getStatus({}, (r3) => {
                    SpectrumService.getActiveProjectionList({}, (r4) => {
                        SpectrumService.getStoredProjectionList({}, (r5) => {
                            SpectrumService.getGBData({}, (r6) => {
                                SpectrumService.getResultMenus({}, (r8) => {
                                    this.props.onStateChange({
                                        serverAlive       : r1,

                                        sessionExists     : r3.sessionExists,
                                        disconnectWarning : r3.disconnectWarning,
                                        TimeToLive        : r3.TimeToLive,
                                        TimeIdle          : r3.TimeIdle,
                                        TimeExpire        : r3.TimeExpire,
                                        TimeTotal         : r3.TimeTotal,

                                        activeProjections : r4,
                                        storedProjections : r5,
                                        GB                : r6,
                                        countryList       : r7,
					                    projectCreated    : r4.length > 0,
                                        projectionName    : (r4.length > 0) ? r4[0].name : '',

                                        DPTree            : r8.TreeDP,
                                        AMTree            : r8.TreeAM
                                    }, () => successFn({
                                        r1 : r1,
                                        r3 : r3,
                                        r4 : r4,
                                        r5 : r5,
                                        r6 : r6,
                                        r7 : r7,
                                        r8 : r8 
                                    }));
                                }, errorFn);
                            }, errorFn);
                        }, errorFn);
                    }, errorFn);
                }, errorFn);
            }, errorFn);
        }, errorFn);
    };
    
    onFetchSessionInputs = (params, successFn, errorFn) => {
        
        let theParams = {
            firstProj : params.firstProj,
            finalProj : params.finalProj
        };
        
        RESTService.ping({}, (r1) => {
            this.onGetModvarsAndEditorsFlag(theParams.firstProj, theParams.finalProj, (r9) => {
                this.props.onStateChange({
                    serverAlive       : r1,
                                                
                    DPModvars         : r9.DPModvars,
                    DPEditors         : r9.DPEditors, 
                    AMModvars         : r9.AMModvars, 
                    AMEditors         : r9.AMEditors,
                }, () => successFn({
                    r1 : r1,
                    r9 : r9,
                }));
            }, errorFn);
        }, errorFn);
    };

    onFetchSessionResults = (params, successFn, errorFn) => {
        RESTService.ping({}, (r1) => {
            SpectrumService.calculateProjection({Proj: 0}, () => {
                SpectrumService.getAllResults({}, (r10) => {
                    this.props.onStateChange({
                        serverAlive       : r1,

                        DPResults         : r10.Results.filter(x => (x.Info.modID === 1) ? true : false), 
                        AMResults         : r10.Results.filter(x => (x.Info.modID === 4) ? true : false), 
                    }, () => successFn({
                        r1 : r1,
                        r10 : r10
                    }));
                }, errorFn);
            }, errorFn);
        }, errorFn);
    };

    //----------------------------------------------------------------

    onSendSession = (params, successFn, errorFn) => {

        let theParams = {
            firstProj : 1,
            finalProj : this.props.appState.GB.FinalProj
        };

        RESTService.ping({}, (r1) => {
            this.onSetModvarsAndEditorsFlag(theParams.firstProj, theParams.finalProj, (r9) => {
                this.props.onStateChange({
                    // nothing
                }, () => successFn({
                    r1 : r1,
                    r9 : r9,
                }));
            }, errorFn);
        }, errorFn);
    };

    onSendAndFetchSession = (params, successFn, errorFn) => {

        let theParams = {
            firstProj : this.props.appState.GB.FirstProj,
            finalProj : this.props.appState.GB.FinalProj
        };

        RESTService.ping({}, (r1) => {
            this.onSendSession(theParams, (r2) => {
                this.onFetchSession({}, (r3) => {
                    successFn({
                        serverAlive  : r1,
                        sendSession  : r2,
                        fetchSession : r3
                    });
                });
            }, errorFn);
        }, errorFn);
    };

    //----------------------------------------------------------------

    onCreateProjectionFlag = (params, successFn, errorFn) => {

        let theParams = {
            CountryISO : params.CountryISO,
            FirstYear  : params.FirstYear,
            FinalYear  : params.FinalYear,
            FileName   : params.FileName,
            ModList    : params.ModList
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.createProjection(theParams, (response) => {
                SpectrumService.getActiveProjectionList({}, (r4) => {
                    SpectrumService.getGBData({}, (r6) => {
                        this.onGetModvarsAndEditorsFlag(1, r6.FinalProj, (r9) => {
                            this.props.onStateChange({
                                serverAlive       : r1,

                                activeProjections : r4,
                                GB                : r6,
                                projectCreated    : r4.length > 0,
                                projectionName    : (r4.length > 0) ? r4[0].name : '',
                                                            
                                DPModvars         : r9.DPModvars,
                                DPEditors         : r9.DPEditors, 
                                AMModvars         : r9.AMModvars, 
                                AMEditors         : r9.AMEditors,
                            }, () => successFn({
                                response          : response,
                                serverAlive       : r1,

                                activeProjections : r4,
                                GB                : r6,
                                projectCreated    : r4.length > 0,
                                projectionName    : (r4.length > 0) ? r4[0].name : '',
                                                            
                                DPModvars         : r9.DPModvars,
                                DPEditors         : r9.DPEditors, 
                                AMModvars         : r9.AMModvars, 
                                AMEditors         : r9.AMEditors,
                            }));
                        }, errorFn);
                    }, errorFn);
                }, errorFn);
            }, errorFn);
        }, errorFn);
    };

    onModifyProjectionFlag = (params, successFn, errorFn) => {

        let theParams = {
            Proj       : params.Proj,
            CountryISO : params.CountryISO,
            FinalYear  : params.FinalYear,
            FileName   : params.FileName,
            ModList    : params.ModList
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.modifyProjection(theParams, (response) => {
                SpectrumService.getActiveProjectionList({}, (r4) => {
                    SpectrumService.getGBData({}, (r6) => {
                        this.onGetModvarsAndEditorsFlag(1, r6.FinalProj, (r9) => {
                            this.props.onStateChange({
                                serverAlive       : r1,

                                activeProjections : r4,
                                GB                : r6,

                                CSModvars         : r9.CSModvars,
                                CSEditors         : r9.CSEditors,

                                projectCreated    : r4.length > 0,
                                projectionName    : (r4.length > 0) ? r4[0].name : '',
                            }, () => successFn({
                                response          : response,
                                serverAlive       : r1,

                                activeProjections : r4,
                                GB                : r6,

                                CSModvars         : r9.CSModvars,
                                CSEditors         : r9.CSEditors,
                                projectCreated    : r4.length > 0,
                                projectionName    : (r4.length > 0) ? r4[0].name : '',
                            }));
                        }, errorFn);
                    }, errorFn);
                }, errorFn);
            }, errorFn);
        }, errorFn);
    };

    onOpenProjectionFlag = (params, successFn, errorFn) => {

        let theParams = {
            FileTitle : params.FileTitle
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.openProjection(theParams, (response) => {
                SpectrumService.getActiveProjectionList({}, (r4) => {
                    SpectrumService.getGBData({}, (r6) => {
                        this.onGetModvarsAndEditorsFlag(1, r6.FinalProj, (r9) => {
                            this.props.onStateChange({
                                serverAlive       : r1,
                                activeProjections : r4,
                                GB                : r6,
                                DPModvars         : r9.DPModvars,
                                DPEditors         : r9.DPEditors, 
                                AMModvars         : r9.AMModvars, 
                                AMEditors         : r9.AMEditors, 
                                projectCreated    : r4.length > 0,
                                projectionName    : (r4.length > 0) ? r4[0].name : '',
                            }, () => successFn({
                                response          : response,
                                serverAlive       : r1,
                                activeProjections : r4,
                                GB                : r6,   
                                DPModvars         : r9.DPModvars,
                                DPEditors         : r9.DPEditors, 
                                AMModvars         : r9.AMModvars, 
                                AMEditors         : r9.AMEditors, 
                                projectCreated    : r4.length > 0,
                                projectionName    : (r4.length > 0) ? r4[0].name : '',
                            }));
                        }, errorFn);
                    }, errorFn);
                }, errorFn);
            }, errorFn);
        }, errorFn);
    }; 

    onSaveProjectionFlag = (params, successFn, errorFn) => {

        let theParams = {
            Proj      : params.Proj,
            FileTitle : params.FileTitle
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.saveProjection(theParams, (response) => {
                SpectrumService.getStoredProjectionList({}, (r5) => {
                    this.props.onStateChange({
                        serverAlive       : r1,
                        storedProjections : r5
                    }, () => successFn({
                        response          : response,
                        serverAlive       : r1,
                        storedProjections : r5
                    }));
                });
            }, errorFn);
        }, errorFn);
    }; 

    onCloseProjectionFlag = (params, successFn, errorFn) => {

        let theParams = {
            Proj : params.Proj
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.closeProjection(theParams, (response) => {
                SpectrumService.getActiveProjectionList({}, (r4) => {
                    SpectrumService.getGBData({}, (r6) => {
                        this.onGetModvarsAndEditorsFlag(1, r6.FinalProj, (r9) => {
                            this.props.onStateChange({
                                projectCreated    : false,
                                projectionName    : '',
                                projectionNotes   : '',
                                serverAlive       : r1,
                                activeProjections : r4,
                                GB                : r6, 
                                DPModvars         : r9.DPModvars,
                                DPEditors         : r9.DPEditors, 
                                AMModvars         : r9.AMModvars, 
                                AMEditors         : r9.AMEditors,
                            }, () => successFn({
                                response          : response,
                                projectCreated    : false,
                                projectionName    : '',
                                projectionNotes   : '',                                
                                serverAlive       : r1,
                                activeProjections : r4,
                                GB                : r6, 
                                DPModvars         : r9.DPModvars,
                                DPEditors         : r9.DPEditors, 
                                AMModvars         : r9.AMModvars, 
                                AMEditors         : r9.AMEditors,
                            }));
                        }, errorFn);
                    }, errorFn);
                }, errorFn);
            }, errorFn);
        }, errorFn);
    };

    onDeleteProjectionFlag = (params, successFn, errorFn) => {
        
        let theParams = {
            FileTitle : params.FileTitle
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.deleteProjection(theParams, (response) => {
                SpectrumService.getStoredProjectionList({}, (r4) => {
                    this.props.onStateChange({
                        serverAlive       : r1,
                        storedProjections : r4,
                    }, () => successFn({
                        response          : response,
                        serverAlive       : r1,
                        storedProjections : r4,
                    }));
                }, errorFn);
            }, errorFn);
        }, errorFn);
    }; 

    onCalculateProjectionFlag = (params, successFn, errorFn) => {
        
        let theParams = {
            Proj : params.Proj
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.calculateProjection(theParams, (response) => {
                //SpectrumService.getAllResults({}, (r10) => {
                    this.props.onStateChange({
                        serverAlive       : r1,
                        //DPResults         : r10.Results.filter(x => (x.Info.modID === 1) ? true : false), 
                        //AMResults         : r10.Results.filter(x => (x.Info.modID === 4) ? true : false), 
                    }, () => successFn({
                        response          : response,
                        serverAlive       : r1,   
                        //DPResults         : r10.Results.filter(x => (x.Info.modID === 1) ? true : false), 
                        //AMResults         : r10.Results.filter(x => (x.Info.modID === 4) ? true : false), 
                    }));
                //}, errorFn);
            }, errorFn);
        }, errorFn);
    }; 

    onDownloadProjectionFlag = (params, successFn, errorFn) => {

        let theParams = {
            FileTitle : params.FileTitle
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.downloadProjection(theParams, (response) => {
                successFn(response);
            }, errorFn);
        }, errorFn);
    };

    onImportProjectionFlag = (params, successFn, errorFn) => {

        // let theParams = {
        //     file : params.file
        // };
        //
        // RESTService.ping({}, (r1) => {
        //     SpectrumService.importProjection(theParams, (response) => {
        //         SpectrumService.getActiveProjectionList({}, (r4) => {
        //             SpectrumService.getGBData({}, (r6) => {
        //                 this.onGetModvarsAndEditorsFlag(1, r6.FinalProj, (r9) => {
        //                     this.props.onStateChange({
        //                         ActiveProjection  : response,
        //                         serverAlive       : r1,
        //
        //                         ActiveProjections : r4,
        //                         GB                : r6,
        //
        //                         CSModvars         : r9.CSModvars,
        //                         CSEditors         : r9.CSEditors,
        //                     }, () => successFn({
        //                         response          : response,
        //                         ActiveProjection  : response,
        //                         serverAlive       : r1,
        //
        //                         ActiveProjections : r4,
        //                         GB                : r6,
        //
        //                         CSModvars         : r9.CSModvars,
        //                         CSEditors         : r9.CSEditors,
        //                     }));
        //                 }, errorFn);
        //             }, errorFn);
        //         }, errorFn);
        //     }, errorFn);
        // }, errorFn);
    };

    //----------------------------------------------------------------

    onGetModvarsAndEditorsFlag = (firstProj, finalProj, successFn, errorFn) => {
 
        let promises = [];
        
        for (let p = firstProj; p <= finalProj; p++) {

            let DPModvars = AppTemplate.DPModvars.map(x => ({ ...x, proj : p }));
            let DPEditors = AppTemplate.DPEditors.map(x => ({ ...x, proj : p }));
            let AMModvars = AppTemplate.AMModvars.map(x => ({ ...x, proj : p }));
            let AMEditors = AppTemplate.AMEditors.map(x => ({ ...x, proj : p }));

            promises.push(new Promise(function (resolve, reject) {
                SpectrumService.getModvars({arr : DPModvars}, 
		    (response) => {
                        resolve(response);
                    }, 
                    (msg) => {
                        reject(msg);  
                    }
                );
            }));
            
            promises.push(new Promise(function (resolve, reject) {
                SpectrumService.getEditors({arr : DPEditors}, 
		    (response) => {
                        resolve(response);
                    }, 
                    (msg) => {
                        reject(msg);  
                    }
                );
            }));

            promises.push(new Promise(function (resolve, reject) {
                SpectrumService.getModvars({arr : AMModvars}, 
		    (response) => {
                        resolve(response);
                    }, 
                    (msg) => {
                        reject(msg);  
                    }
                );
            }));
            
            promises.push(new Promise(function (resolve, reject) {
                SpectrumService.getEditors({arr : AMEditors}, 
		    (response) => {
                        resolve(response);
                    }, 
                    (msg) => {
                        reject(msg);  
                    }
                );
            }));

        }

        Promise.all(promises)
            .then((responses) => {
                
                let arr = {};
                arr.DPModvars = [];
                arr.DPEditors = []; 
                arr.AMModvars = []; 
                arr.AMEditors = []; 

                for (let p = 1; p <= finalProj; p++) {
                    arr.DPModvars.push(...responses[0]);
                    arr.DPEditors.push(...responses[1]); 
                    arr.AMModvars.push(...responses[2]); 
                    arr.AMEditors.push(...responses[3]); 
                }

                successFn(arr);
            })
            .catch((e) => {
                console.error(e);
                errorFn(e);
            });
    };

    onSetModvarsAndEditorsFlag = (firstProj, finalProj, successFn, errorFn) => {
 
        let promises = [];

        for (let p = firstProj; p <= finalProj; p++) {

            let DPModvars = this.props.appState.DPModvars.filter(x => (x.proj === p) ? true : false);
            let DPEditors = this.props.appState.DPEditors.filter(x => (x.proj === p) ? true : false);
            let AMModvars = this.props.appState.AMModvars.filter(x => (x.proj === p) ? true : false);
            let AMEditors = this.props.appState.AMEditors.filter(x => (x.proj === p) ? true : false);
            
            // Give the server only what it wants
            DPEditors = DPEditors.map(x => ({ proj : x.proj, modID : x.modID, editorMstID : x.editorMstID, tableData : x.tableData, ...x }));
            AMEditors = AMEditors.map(x => ({ proj : x.proj, modID : x.modID, editorMstID : x.editorMstID, tableData : x.tableData, ...x }));

            promises.push(new Promise(function (resolve, reject) {
                SpectrumService.setEditors({arr : DPEditors},
                    (responseModvars) => {
                        SpectrumService.setModvars({arr : DPModvars}, 
                            (responseEditors) => {
                                resolve(responseModvars, responseEditors);
                            }, 
                            (msg) => {
                                reject(msg);  
                            }
                        );
                    }, 
                    (msg) => {
                        reject(msg);  
                    }
                );
            }));


            promises.push(new Promise(function (resolve, reject) {
                SpectrumService.setEditors({arr : AMEditors},
                    (responseModvars) => {
                        SpectrumService.setModvars({arr : AMModvars}, 
                            (responseEditors) => {
                                resolve(responseModvars, responseEditors);
                            }, 
                            (msg) => {
                                reject(msg);  
                            }
                        );
                    }, 
                    (msg) => {
                        reject(msg);  
                    }
                );
            }));
        }

        Promise.all(promises)
            .then((responseModvars, responseEditors) => {
                
                let arr = {};
                arr.DPModvarsResponse = [];
                arr.DPEditorsResponse = []; 
                arr.AMModvarsResponse = []; 
                arr.AMEditorsResponse = []; 

                for (let p = 1; p <= finalProj; p++) {

                    arr.DPModvarsResponse.push(responseModvars[0]);
                    arr.DPEditorsResponse.push(responseEditors[1]); 
                    arr.AMModvarsResponse.push(responseModvars[2]); 
                    arr.AMEditorsResponse.push(responseEditors[3]); 
                }

                successFn(arr);
            })
            .catch((e) => {
                console.error(e);
                errorFn(e);
            });
    };

    onGetSelectedResultsFlag = (params, successFn, errorFn) => {

        let theParams = {
            results : params.results
        };

        RESTService.ping({}, (r1) => {
            SpectrumService.getResultTables(theParams, (response1) => {
                SpectrumService.getResultCharts(theParams, (response2) => {

                    function isNumeric(n) {
                        return !isNaN(parseFloat(n)) && isFinite(n);
                    }

                    /* Remove titles from the charts */
                    for (let i = 0; i < response2.length; i++) {
                        if (i !== 4)
                            delete response2[i].result.title;
                    }

                    /* mstID 11 should be stacked bar */
                    response2[0].result.chartType = 'line';
                    response2[1].result.chartType = 'line';
                    response2[3].result.chartType = 'line';
                    response2[4].result.chartType = 'line';
                    response2[5].result.chartType = 'line';
                    response2[6].result.chartType = 'line';
                    response2[7].result.chartType = 'line';

                    response2[2].result.stacking = 'normal';
                    response2[2].result.chartType = 'column';

                    response2[3] = response2[4];

                    /* Round the table values */
                    for (let j = 0; j < response1.length; j++) {
                        for (let r = 1; r < response1[j].result.GBRowCount; r++) {
                            for (let c = 1; c < response1[j].result.GBColCount; c++) {

                                let value = response1[j].result.tableData.value[r][c];

                                /* For Mortality rates, use 2 decimals */
                                if (j !== 3 ) {
                                    if (isNumeric(value)) {
                                        value = value.toFixed(0);
                                    }
                                } else {
                                    if (isNumeric(value)) {
                                        value = value.toFixed(2);
                                    }
                                }

                                response1[j].result.tableData.value[r][c] = value;
                            }
                        }
                    }
                    
                    this.props.onStateChange(
                        {
                            serverAlive        : r1,
                            selectedPackTables : response1,
                            selectedPackCharts : response2,
                        }, 
                        () => successFn({
                            serverAlive        : r1,
                            response1          : response1,
                            response2          : response2,
                        })
                    ); 

                }, errorFn);
            }, errorFn);
        }, errorFn);
    };



































    onGetModvarsAndEditors = (firstProj, finalProj, successFn, errorFn) => {

        let that = this;
        let promises = [];
        
        for (let p = firstProj; p <= finalProj; p++) {

            let DPModvars = AppTemplate.DPModvars.map(x => ({ ...x, proj : p }));
            let DPEditors = AppTemplate.DPEditors.map(x => ({ ...x, proj : p }));
            let AMModvars = AppTemplate.AMModvars.map(x => ({ ...x, proj : p }));
            let AMEditors = AppTemplate.AMEditors.map(x => ({ ...x, proj : p }));

            promises.push(new Promise(function (resolve, reject) {
                that.onGetModvars({arr : DPModvars}, (response) => {  
                    resolve(response);
                });
            }));
            
            promises.push(new Promise(function (resolve, reject) {
                that.onGetEditors({arr : DPEditors}, (response) => {  
                    resolve(response);
                });
            }));

            promises.push(new Promise(function (resolve, reject) {
                that.onGetModvars({arr : AMModvars}, (response) => {  
                    resolve(response);
                });
            }));
            
            promises.push(new Promise(function (resolve, reject) {
                that.onGetEditors({arr : AMEditors}, (response) => {  
                    resolve(response);
                });
            }));

        }

        Promise.all(promises)
            .then((responses) => {
                
                let arr = {};
                arr.DPModvars = [];
                arr.DPEditors = []; 
                arr.AMModvars = []; 
                arr.AMEditors = []; 

                for (let p = 1; p <= finalProj; p++) {
                    arr.DPModvars.push(...responses[0]);
                    arr.DPEditors.push(...responses[1]); 
                    arr.AMModvars.push(...responses[2]); 
                    arr.AMEditors.push(...responses[3]); 
                }

                successFn(arr);
            })
            .catch((e) => {
                console.error(e);
                errorFn(e);
            });
    };

    onSetModvarsAndEditors = (firstProj, finalProj, successFn, errorFn) => {

        let that = this;
        let promises = [];

        for (let p = firstProj; p <= finalProj; p++) {

            let DPModvars = this.props.appState.DPModvars.filter(x => (x.proj === p) ? true : false);
            let DPEditors = this.props.appState.DPEditors.filter(x => (x.proj === p) ? true : false);
            let AMModvars = this.props.appState.AMModvars.filter(x => (x.proj === p) ? true : false);
            let AMEditors = this.props.appState.AMEditors.filter(x => (x.proj === p) ? true : false);
            
            // Give the server only what it wants
            DPEditors = DPEditors.map(x => ({ proj : x.proj, modID : x.modID, editorMstID : x.editorMstID, tableData : x.tableData }));
            AMEditors = AMEditors.map(x => ({ proj : x.proj, modID : x.modID, editorMstID : x.editorMstID, tableData : x.tableData }));

            promises.push(new Promise(function (resolve, reject) {
                that.onSetModvars({arr : DPModvars}, (response) => {  
                    resolve(response);
                });
            }));
            
            promises.push(new Promise(function (resolve, reject) {
                that.onSetEditors({arr : DPEditors}, (response) => {  
                    resolve(response);
                });
            }));

            promises.push(new Promise(function (resolve, reject) {
                that.onSetModvars({arr : AMModvars}, (response) => {  
                    resolve(response);
                });
            }));
            
            promises.push(new Promise(function (resolve, reject) {
                that.onSetEditors({arr : AMEditors}, (response) => {  
                    resolve(response);
                });
            }));

        }

        Promise.all(promises)
            .then((responses) => {
                
                let arr = {};
                arr.DPModvarsResponse = [];
                arr.DPEditorsResponse = []; 
                arr.AMModvarsResponse = []; 
                arr.AMEditorsResponse = []; 

                for (let p = 1; p <= finalProj; p++) {
                    arr.DPModvarsResponse.push(responses[0]);
                    arr.DPEditorsResponse.push(responses[1]); 
                    arr.AMModvarsResponse.push(responses[2]); 
                    arr.AMEditorsResponse.push(responses[3]); 
                }

                successFn(arr);
            })
            .catch((e) => {
                console.error(e);
                errorFn(e);
            });
    };

























































    











    //==================================================================================================================
    //
    //                                            API RESTService Calls
    //
    //==================================================================================================================

    onPing = (params, successFn, errorFn) => {
        RESTService.ping(params, (response) => {
            this.props.onStateChange({
                serverAlive : response
            }, () => successFn(response));
        }, (msg) => {
            this.props.onStateChange({
                serverAlive : false
            }, () => errorFn(msg));
        });
    };     
    
    onGetVersion = (params, successFn, errorFn) => {
        RESTService.getVersion(params, (response) => {
            this.props.onStateChange({
                version : response
            }, () => successFn(response));
        }, (msg) => {
            this.props.onStateChange({
                version : "unknown"
            }, () => errorFn(msg));
        });
    }; 

    onGetCountries = (params, successFn, errorFn) => {
        RESTService.getCountries(params, (response) => {
            this.props.onStateChange({
                countryList : response
            }, () => successFn(response));
        }, errorFn);
    };

    //==================================================================================================================
    //
    //                                            API LoginService Calls
    //
    //==================================================================================================================

    onGetStatus = (params, successFn, errorFn) => {
        LoginService.getStatus(params, (response) => {
            this.props.onStateChange({
                sessionExists     : response.sessionExists,
                disconnectWarning : response.disconnectWarning,
                TimeToLive        : response.TimeToLive,
                TimeIdle          : response.TimeIdle,
                TimeExpire        : response.TimeExpire,
                TimeTotal         : response.status.time
            }, () => successFn(response));
        }, errorFn);
    }; 

    onSignedIn = (params, successFn, errorFn) => {
        LoginService.signedIn(params, (response) => {
            if (response === true) {
                this.props.onStateChange({
                    signedIn  : true
                }, () => successFn(response));
            }
            else {
                this.props.onStateChange({
                    humanName : '',
                    signedIn  : false,
                    isGuest   : false,
                }, () => successFn(response));
            }
        }, errorFn);
    }; 

    onLogin = (params, successFn, errorFn) => {
        LoginService.login(params, (response) => {
            if (response === true) {
                this.onFetchSession({}, () => {
                    this.props.onStateChange({
                        humanName     : params.username,
                        signedIn      : true,
                        isGuest       : false,
                        sessionExists : true
                    }, () => successFn(response));
                }, errorFn);
            }
            else {
                successFn(response);
            }
        }, errorFn);
    }; 

    onLoginAsGuest = (params, successFn, errorFn) => {
        LoginService.loginAsGuest(params, (response) => {
            if (response === true) {
                this.onFetchSession({}, () => {
                    this.props.onStateChange({
                        humanName     : 'Guest',
                        signedIn      : true,
                        isGuest       : true,
                        sessionExists : true
                    }, () => successFn(response));
                }, errorFn);
            }
            else {
                successFn(response);
            }
        }, errorFn);
    }; 

    onLogout = (params, successFn, errorFn) => {
        LoginService.logout(params, (response) => {
            if (response === true) {
                this.props.onStateChange({
                    humanName     : '',
                    signedIn      : false,
                    isGuest       : false,
                    pageID        : 'GBLoggedOutFm',
                    sessionExists : false
                }, () => successFn(response));
            }
            else {
                console.log('Could not log out!');
                successFn(response);
            }
        }, errorFn);
    }; 

    onGetAccountInfo = (params, successFn, errorFn) => {
        LoginService.getAccountInfo(params, (response) => {
            this.props.onStateChange({
                signedIn  : response.fSignedIn,
                userName  : response.fUserName,
                humanName : response.fHumanName,
                isGuest   : response.fIsGuest,
                isAdmin   : response.fIsAdmin,
            }, () => successFn(response));
        }, errorFn);
    }; 

    //==================================================================================================================
    //
    //                                           API SpectrumService Calls
    //
    //==================================================================================================================

    onKeepAlive = (params, successFn, errorFn) => {
        SpectrumService.keepAlive(params, (response) => {
            this.props.onStateChange({
                keepAlive : response
            }, () => successFn(response));
        }, errorFn);
    }; 

    onGetEverything = (params, successFn, errorFn) => {
        SpectrumService.getEverything(params, (response) => {
            this.props.onStateChange({
                signedIn          : response.accountInfo.fSignedIn,
                userName          : response.accountInfo.fUserName,
                humanName         : response.accountInfo.fHumanName,
                isGuest           : response.accountInfo.fIsGuest,
                isAdmin           : response.accountInfo.fIsAdmin,

                // sessionExists     : r3.sessionExists,
                // disconnectWarning : r3.disconnectWarning,
                // TimeToLive        : r3.TimeToLive,
                // TimeIdle          : r3.TimeIdle,
                // TimeExpire        : r3.TimeExpire,
                // TimeTotal         : r3.TimeTotal,

                activeProjections : response.activeProjections,
                storedProjections : response.storedProjections,
                GB                : response.GB,
                countryList       : response.GB.CountryRegionObjList,

                // DPTree            : r8.TreeDP,
                // AMTree            : r8.TreeAM,
            
                // DPModvars         : response.projections.DPModvars,
                // DPEditors         : response.projections.DPEditors, 
                // AMModvars         : response.projections.AMModvars, 
                // AMEditors         : response.projections.AMEditors, 

                // DPResults         : r10.Results.filter(x => (x.Info.modID === 1) ? true : false), 
                // AMResults         : r10.Results.filter(x => (x.Info.modID === 4) ? true : false), 
            }, () => successFn(response));
        }, errorFn);
    }; 

    onGetActiveProjectionList = (params, successFn, errorFn) => {
        SpectrumService.getActiveProjectionList(params, (response) => {
            this.props.onStateChange({
                activeProjections : response
            }, () => successFn(response));
        }, errorFn);
    }; 

    onGetStoredProjectionList = (params, successFn, errorFn) => {
        SpectrumService.getStoredProjectionList(params, (response) => {
            this.props.onStateChange({
                storedProjections : response
            }, () => successFn(response));
        }, errorFn);
    }; 

    //----------------------------------------------------------------

    onGetGBData = (params, successFn, errorFn) => {
        SpectrumService.getGBData(params, (response) => {
            this.props.onStateChange({
                GB : response
            }, () => successFn(response));
        }, errorFn);
    }; 

    onGetModvars = (params, successFn, errorFn) => {

        SpectrumService.getModvars(params, (response) => {
            let DPModvars = AppTemplate.DPModvars.map(x => ({ ...x, proj : 1 }));
            let AMModvars = AppTemplate.AMModvars.map(x => ({ ...x, proj : 1 }));

            for (let i = 0; i < response.length; i++) {

                let modvar = response[i];
                let tag = modvar.tag;
                let pos1 = DPModvars.map(function(e) { return e.tag }).indexOf(tag);
                let pos2 = AMModvars.map(function(e) { return e.tag }).indexOf(tag);

                if (pos1 !== -1) {
                    DPModvars[pos1] = modvar;
                }
                
                if (pos2 !== -1) {
                    AMModvars[pos2] = modvar;
                }
            }

            this.props.onStateChange({        
                DPModvars : DPModvars,
                AMModvars : AMModvars, 
            }, () => successFn(response));
        }, errorFn);
    }; 

    onSetModvars = (params, successFn, errorFn) => {
        SpectrumService.setModvars(params, (response) => {
            successFn(response);
        }, errorFn);
    }; 

    onGetEditors = (params, successFn, errorFn) => {
        SpectrumService.getEditors(params, (response) => {
            let DPEditors = AppTemplate.DPEditors.map(x => ({ ...x, proj : 1 }));
            let AMEditors = AppTemplate.AMEditors.map(x => ({ ...x, proj : 1 }));

            for (let i = 0; i < response.length; i++) {

                let packTable = response[i];
                let editorMstID = packTable.editorMstID;
                let pos1 = DPEditors.map(function(e) { return e.editorMstID }).indexOf(editorMstID);
                let pos2 = AMEditors.map(function(e) { return e.editorMstID }).indexOf(editorMstID);

                if (pos1 !== -1) {
                    DPEditors[pos1] = packTable;
                }
                
                if (pos2 !== -1) {
                    AMEditors[pos2] = packTable;
                }
            }

            this.props.onStateChange({        
                DPModvars : DPEditors,
                AMModvars : AMEditors, 
            }, () => successFn(response));
        }, errorFn);
    }; 

    onSetEditors = (params, successFn, errorFn) => {
        SpectrumService.setEditors(params, (response) => {
            successFn(response);
        }, errorFn);
    }; 

    //----------------------------------------------------------------

    onCreateProjection = (params, successFn, errorFn) => {
        SpectrumService.createProjection(params, (response) => {
            this.props.onStateChange({
                projectCreated   : true
            }, () => successFn(response));
        }, errorFn);
    };

    onModifyProjection = (params, successFn, errorFn) => {
        SpectrumService.modifyProjection(params, (response) => {
            this.props.onStateChange({
                //
            }, () => successFn(response));
        }, errorFn);
    };

    onOpenProjection = (params, successFn, errorFn) => {
        SpectrumService.openProjection(params, (response) => {
            this.props.onStateChange({
                projectCreated   : true
            }, () => successFn(response));
        }, errorFn);
    }; 

    onSaveProjection = (params, successFn, errorFn) => {
        SpectrumService.saveProjection(params, (response) => {
            alert(response);
            successFn(response);
        }, errorFn);
    }; 

    onCloseProjection = (params, successFn, errorFn) => {
        SpectrumService.closeProjection(params, (response) => {
            alert(response);
            successFn(response);
        }, errorFn);
    }; 

    onDownloadProjection = (params, successFn, errorFn) => {
        SpectrumService.downloadProjection(params, (response) => {

            let url = API_URL_BASE + '/' + response;
            let a = document.createElement("a");
            a.href = url;
            let fileName = url.split("/").pop();
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();

            successFn(response);
        }, errorFn);
    }; 

    onDeleteProjection = (params, successFn, errorFn) => {
        SpectrumService.deleteProjection(params, (response) => {
            successFn(response);
        }, errorFn);
    }; 

    onCalculateProjection = (params, successFn, errorFn) => {
        SpectrumService.calculateProjection(params, (response) => {
            successFn(response);
        }, errorFn);
    }; 

    //----------------------------------------------------------------

    onGetResultsData = (params, successFn, errorFn) => {
        SpectrumService.getResultsData(params, (response) => {
            this.props.onStateChange({
                // ???
            }, () => successFn(response));
        }, errorFn);
    }; 

    onGetResultTable = (params, successFn, errorFn) => {
        SpectrumService.getResultTable(params, (response) => {
            this.props.onStateChange({
                // ???
            }, () => successFn(response));
        }, errorFn);
    }; 

    onGetAllResults = (params, successFn, errorFn) => {
        SpectrumService.getAllResults(params, (response) => {
            this.props.onStateChange({
                results : response
            }, () => successFn(response));
        }, errorFn);
    }; 

    onGetResultMenus = (params, successFn, errorFn) => {
        SpectrumService.getResultMenus(params, (response) => {
            this.props.onStateChange({
                resultMenus : response
            }, () => successFn(response));
        }, errorFn);
    }; 

    //==================================================================================================================
    //
    //                                                Render
    //
    //==================================================================================================================

    render() {
        return null;
    }
}

export default APIService;